import React, { useState, useRef, useEffect, Fragment } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { Toast } from 'primereact/toast';
import { Tag } from 'primereact/tag';
import { Toolbar } from 'primereact/toolbar';
import { Dialog } from 'primereact/dialog';
import { InputTextarea } from 'primereact/inputtextarea';
import { Calendar } from 'primereact/calendar';
import { Checkbox } from 'primereact/checkbox';
import { Timeline } from 'primereact/timeline';
import { Chip } from '@mui/joy';
import { RouteComponentProps } from '../../models/RouteComponent';
import { Typography, Grid, Box, Card } from '@mui/joy';

// Material Design Icons
import WarningIcon from '@mui/icons-material/Warning';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';

// Non-compliance service
import {
    NonComplianceIssue,
    KpiData,
    SiteEmployee,
    getKpiData,
    getNonComplianceIssues,
    resolveIssue,
    exportNonComplianceData,
    getSiteEmployees
} from '../../service/nonComplianceService';

/**
 * Props interface for the StatCard component - matches client dashboard pattern
 */
interface StatCardProps {
    title: string;
    value: string | number;
    icon: React.ReactNode;
    color: "primary" | "success" | "warning" | "danger" | "neutral";
    onClick?: () => void;
}

/**
 * StatCard Component - Displays a metric card with optional click functionality
 * Matches client dashboard pattern for consistency
 */
const StatCard = ({ title, value, icon, color, onClick }: StatCardProps) => {
    return (
        <Card
            variant="outlined"
            onClick={onClick}
            sx={{
                display: "flex",
                flexDirection: "column",
                height: "100%",
                transition: "all 0.2s",
                cursor: onClick ? "pointer" : "default",
                "&:hover": {
                    boxShadow: onClick ? "lg" : "md",
                    borderColor: onClick ? "primary.outlinedHoverBorder" : "neutral.outlinedHoverBorder",
                    transform: onClick ? "translateY(-2px)" : "none",
                },
            }}
        >
            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                <Typography level="body-sm" textColor="text.secondary">
                    {title}
                </Typography>
                <Box
                    sx={{
                        backgroundColor: `${color}.softBg`,
                        color: `${color}.solidColor`,
                        borderRadius: "50%",
                        width: 35,
                        height: 35,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    {icon}
                </Box>
            </Box>
            <Typography level="h3">{value}</Typography>
        </Card>
    );
};

interface FilterOptions {
    status: string;
    priority: string;
    category: string;
    assignedTo: string;
}

function NonComplianceTracker(props: RouteComponentProps) {
    const toast = useRef<Toast>(null);
    const [selectedIssues, setSelectedIssues] = useState<NonComplianceIssue[]>([]);
    const [kpiData, setKpiData] = useState<KpiData | null>(null);
    const [issues, setIssues] = useState<NonComplianceIssue[]>([]);
    const [loading, setLoading] = useState(true);
    const [totalRecords, setTotalRecords] = useState(0);
    const [filters, setFilters] = useState<FilterOptions>({
        status: '',
        priority: '',
        category: '',
        assignedTo: ''
    });
    const [first, setFirst] = useState(0);
    const [rows, setRows] = useState(10);

    // Get siteId and clientId from session
    const siteId = props.session?.site_id;
    const clientId = props.session?.client_id;

    // Modal states
    const [resolveModalVisible, setResolveModalVisible] = useState(false);
    const [historyModalVisible, setHistoryModalVisible] = useState(false);
    const [detailsModalVisible, setDetailsModalVisible] = useState(false);
    const [selectedIssueForAction, setSelectedIssueForAction] = useState<NonComplianceIssue | null>(null);
    const [resolutionDetails, setResolutionDetails] = useState('');
    const [resolutionDate, setResolutionDate] = useState<Date | null>(new Date());
    const [resolvedBy, setResolvedBy] = useState('');
    const [notifyInspector, setNotifyInspector] = useState(false);
    const [siteEmployees, setSiteEmployees] = useState<SiteEmployee[]>([]);
    const [loadingEmployees, setLoadingEmployees] = useState(false);

    // Load initial data
    useEffect(() => {
        const initializeData = async () => {
            await loadKpiData();
            loadData();
        };
        initializeData();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Debug effect to track kpiData changes
    useEffect(() => {
        console.log('kpiData state changed:', kpiData);
    }, [kpiData]);    // Load KPI data
    const loadKpiData = async () => {
        try {
            const data = await getKpiData();
            console.log('KPI Data received:', data);
            setKpiData(data || null);
            console.log('KPI Data state should be updated to:', data);
        } catch (error) {
            console.error('Failed to load KPI data:', error);
            // Set fallback KPI data
            setKpiData({
                totalIssues: 0,
                openIssues: 0,
                inProgressIssues: 0,
                resolvedIssues: 0,
                closedIssues: 0,
                overdueIssues: 0,
                avgResolutionTimeHours: 0,
                complianceRate: 0,
                trendsLast30Days: {
                    newIssues: 0,
                    resolvedIssues: 0,
                    complianceRateChange: 0,
                },
                byPriority: {
                    critical: 0,
                    high: 0,
                    medium: 0,
                    low: 0,
                },
                bySite: [],
                byTemplate: [],
            });
            showToast('error', 'Error', 'Failed to load KPI data');
        }
    };

    // Load issues data with pagination and filtering
    const loadData = async (page = 0, pageSize = 10, filterParams = filters) => {
        setLoading(true);
        try {
            console.log('=== loadData called ===');
            console.log('Raw filterParams:', filterParams);
            
            const filteredParams = Object.fromEntries(Object.entries(filterParams).filter(([key, value]) => {
                const shouldKeep = value !== '' && value !== null && value !== undefined;
                console.log(`Filter ${key}: "${value}" -> ${shouldKeep ? 'KEEP' : 'REMOVE'}`);
                return shouldKeep;
            }));
            
            console.log('After filtering empty values:', filteredParams);
            
            // Map frontend filter names to backend API parameter names
            const apiParams: any = {
                page: page + 1, // API uses 1-based pagination
                limit: pageSize,
                // Request all issues by default (including resolved) to match task-dashboard count
                status: 'all'
            };
            
            // Add activeClientId for client scoping
            if (clientId) {
                apiParams.activeClientId = clientId;
                console.log(`Added activeClientId: "${clientId}"`);
            }
            
            // Add siteId if at site level
            if (siteId) {
                apiParams.siteId = siteId;
                console.log(`Added siteId: "${siteId}"`);
            }
            
            // Map priority to priority_level for backend
            if (filteredParams.priority) {
                apiParams.priority_level = filteredParams.priority;
                console.log(`Mapped priority "${filteredParams.priority}" to priority_level`);
            }
            
            // Override status if user explicitly selected one
            if (filteredParams.status) {
                apiParams.status = filteredParams.status;
                console.log(`Added status: "${filteredParams.status}"`);
            }
            if (filteredParams.category) {
                apiParams.category = filteredParams.category;
                console.log(`Added category: "${filteredParams.category}"`);
            }
            if (filteredParams.assignedTo) {
                apiParams.assignedTo = filteredParams.assignedTo;
                console.log(`Added assignedTo: "${filteredParams.assignedTo}"`);
            }
            
            console.log('Final API params being sent:', apiParams);

            const response = await getNonComplianceIssues(apiParams);
            console.log('Issues API response:', response);
            
            // Extract issues array and pagination info from the nested content structure
            const issuesData = Array.isArray(response?.content?.issues) ? response.content.issues : [];
            const total = response?.content?.pagination?.total || 0;
            
            setIssues(issuesData);
            setTotalRecords(total);
        } catch (error) {
            console.error('Failed to load issues:', error);
            // Set empty array on error to prevent DataTable issues
            setIssues([]);
            setTotalRecords(0);
            showToast('error', 'Error', 'Failed to load issues');
        } finally {
            setLoading(false);
        }
    };

    // Handle pagination
    const onPageChange = (event: any) => {
        setFirst(event.first);
        setRows(event.rows);
        loadData(event.page, event.rows);
    };

    // Handle filter changes
    const onFilterChange = (field: keyof FilterOptions, value: string) => {
        console.log(`Filter change: ${field} = "${value}"`);
        console.log('Current filters before change:', filters);
        
        const newFilters = { ...filters, [field]: value };
        console.log('New filters object:', newFilters);
        
        setFilters(newFilters);
        setFirst(0); // Reset to first page
        loadData(0, rows, newFilters);
    };

    // Show toast messages
    const showToast = (severity: 'success' | 'info' | 'warn' | 'error', summary: string, detail: string) => {
        toast.current?.show({ severity, summary, detail });
    };

    // Handle assign issue - UNUSED FUNCTION
    /* const handleAssignIssue = async (issueId: string, assignedTo: string) => {
        try {
            await assignIssue(issueId, { assignedTo });
            showToast('success', 'Success', 'Issue assigned successfully');
            loadData(Math.floor(first / rows), rows);
            loadKpiData();
        } catch (error) {
            showToast('error', 'Error', 'Failed to assign issue');
        }
    }; */

    // Handle resolve issue - UNUSED FUNCTION  
    /* const handleResolveIssue = async (issueId: string, correctiveActions: string) => {
        try {
            await resolveIssue(issueId, { correctiveActions });
            showToast('success', 'Success', 'Issue resolved successfully');
            loadData(Math.floor(first / rows), rows);
            loadKpiData();
        } catch (error) {
            showToast('error', 'Error', 'Failed to resolve issue');
        }
    }; */

    // Handle view history
    const handleViewHistory = (issue: NonComplianceIssue) => {
        setSelectedIssueForAction(issue);
        setHistoryModalVisible(true);
    };

    // Handle mark as resolved
    const handleMarkResolved = async (issue: NonComplianceIssue) => {
        setSelectedIssueForAction(issue);
        // Reset form fields
        setResolutionDetails('');
        setResolutionDate(new Date());
        setResolvedBy('');
        setNotifyInspector(false);
        
        // Load employees for the specific site
        setLoadingEmployees(true);
        try {
            const employees = await getSiteEmployees(issue.siteId);
            setSiteEmployees(employees);
            console.log(`Loaded ${employees.length} employees for site: ${issue.siteName} (${issue.siteId})`);
        } catch (error) {
            console.error('Failed to load site employees:', error);
            showToast('error', 'Error', 'Failed to load site employees');
        }
        setLoadingEmployees(false);
        
        setResolveModalVisible(true);
    };

    // Handle view details
    const handleViewDetails = (issue: NonComplianceIssue) => {
        setSelectedIssueForAction(issue);
        setDetailsModalVisible(true);
    };

    // Handle resolve issue submit from modal
    const handleResolveIssueSubmit = async () => {
        if (!selectedIssueForAction || !resolutionDetails.trim()) {
            showToast('warn', 'Warning', 'Please provide resolution details');
            return;
        }

        if (!resolvedBy) {
            showToast('warn', 'Warning', 'Please select who resolved this issue');
            return;
        }

        try {
            await resolveIssue(selectedIssueForAction.id, {
                correctiveActions: resolutionDetails,
                resolutionDate: resolutionDate?.toISOString(),
                resolvedBy: resolvedBy,
                notes: notifyInspector ? 'Inspector notified of resolution' : undefined
            });
            showToast('success', 'Success', 'Issue resolved successfully');
            setResolveModalVisible(false);
            loadData(Math.floor(first / rows), rows);
            loadKpiData();
        } catch (error) {
            showToast('error', 'Error', 'Failed to resolve issue');
        }
    };

    // Handle bulk actions - HIDDEN FUNCTIONALITY
    /* const handleBulkAction = async (action: string) => {
        if (selectedIssues.length === 0) {
            showToast('warn', 'Warning', 'Please select issues first');
            return;
        }

        try {
            const issueIds = selectedIssues.map(issue => issue.id);
            await bulkAction({
                issueIds,
                action: action as any,
                data: {} // Add specific data based on action
            });

            showToast('success', 'Success', `Bulk ${action} completed successfully`);
            setSelectedIssues([]);
            loadData(Math.floor(first / rows), rows);
            loadKpiData();
        } catch (error) {
            showToast('error', 'Error', `Failed to perform bulk ${action}`);
        }
    }; */

    // Handle export
    const handleExport = async () => {
        try {
            const success = await exportNonComplianceData('csv');
            if (success) {
                showToast('success', 'Success', 'Data exported successfully');
            } else {
                showToast('error', 'Error', 'Failed to export data');
            }
        } catch (error) {
            showToast('error', 'Error', 'Failed to export data');
        }
    };

    // Column templates
    const priorityBodyTemplate = (rowData: NonComplianceIssue) => {
        const getSeverity = (priority: string) => {
            switch (priority) {
                case 'critical': return 'danger';
                case 'high': return 'warning';
                case 'medium': return 'info';
                case 'low': return 'success';
                default: return 'info';
            }
        };

        return <Tag value={rowData.priority} severity={getSeverity(rowData.priority)} />;
    };

    const statusBodyTemplate = (rowData: NonComplianceIssue) => {
        const getSeverity = (status: string) => {
            switch (status) {
                case 'open': return 'danger';
                case 'assigned': return 'warning';
                case 'in_progress': return 'info';
                case 'resolved': return 'success';
                case 'overdue': return 'danger';
                default: return 'info';
            }
        };

        return <Tag value={rowData.status} severity={getSeverity(rowData.status)} />;
    };

    const dateBodyTemplate = (rowData: NonComplianceIssue) => {
        if (!rowData.dueDate) return 'N/A';
        return new Date(rowData.dueDate).toLocaleDateString();
    };

    const actionBodyTemplate = (rowData: NonComplianceIssue) => {
        return (
            <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                    icon="pi pi-eye"
                    className="p-button-rounded p-button-text"
                    onClick={() => handleViewDetails(rowData)}
                    tooltip="View details"
                />
                {rowData.status !== 'resolved' && (
                    <Button
                        icon="pi pi-check"
                        className="p-button-rounded p-button-success p-button-text"
                        onClick={() => handleMarkResolved(rowData)}
                        tooltip="Mark as Resolved"
                    />
                )}
                <Button
                    icon="pi pi-history"
                    className="p-button-rounded p-button-text"
                    onClick={() => handleViewHistory(rowData)}
                    tooltip="View History"
                />
            </Box>
        );
    };

    // Toolbar template
    const leftToolbarTemplate = () => {
        return (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                {/* Hidden bulk action buttons */}
                {/* <Button 
                    label="Assign Selected" 
                    icon="pi pi-user" 
                    className="p-button-success"
                    disabled={selectedIssues.length === 0}
                    onClick={() => handleBulkAction('assign')}
                />
                <Button 
                    label="Resolve Selected" 
                    icon="pi pi-check" 
                    className="p-button-info"
                    disabled={selectedIssues.length === 0}
                    onClick={() => handleBulkAction('resolve')}
                /> */}
                <Button 
                    label="Export" 
                    icon="pi pi-download" 
                    className="p-button-help"
                    onClick={handleExport}
                />
            </Box>
        );
    };

    const rightToolbarTemplate = () => {
        return (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Dropdown
                    value={filters.priority}
                    onChange={(e) => onFilterChange('priority', e.value)}
                    options={[
                        { label: 'All Priority', value: '' },
                        { label: 'Critical', value: 'critical' },
                        { label: 'High', value: 'high' },
                        { label: 'Medium', value: 'medium' },
                        { label: 'Low', value: 'low' }
                    ]}
                    optionLabel="label"
                    optionValue="value"
                    placeholder="Filter by Priority"
                    className="w-full md:w-14rem"
                />
                <Dropdown
                    value={filters.status}
                    onChange={(e) => onFilterChange('status', e.value)}
                    options={[
                        { label: 'All Status', value: '' },
                        { label: 'Open', value: 'open' },
                        { label: 'In Progress', value: 'in_progress' },
                        { label: 'Resolved', value: 'completed' },
                        { label: 'Closed', value: 'closed' }
                    ]}
                    optionLabel="label"
                    optionValue="value"
                    placeholder="Filter by Status"
                    className="w-full md:w-14rem"
                />
            </Box>
        );
    };

    return (
        <Fragment>
            <Toast ref={toast} />
            
            <Typography level="h2" sx={{ mb: 3, fontWeight: 700, color: 'neutral.900' }}>
                Non-Compliance Issues Tracker
            </Typography>

            {/* KPI Cards */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid xs={12} sm={6} md={3}>
                    <StatCard
                        title="Total Issues"
                        value={kpiData?.totalIssues || 0}
                        icon={<ReceiptLongIcon fontSize="small" />}
                        color="primary"
                    />
                </Grid>
                <Grid xs={12} sm={6} md={3}>
                    <StatCard
                        title="Open Issues"
                        value={kpiData?.openIssues || 0}
                        icon={<WarningIcon fontSize="small" />}
                        color="warning"
                    />
                </Grid>
                <Grid xs={12} sm={6} md={3}>
                    <StatCard
                        title="Overdue"
                        value={kpiData?.overdueIssues || 0}
                        icon={<AccessTimeIcon fontSize="small" />}
                        color="danger"
                    />
                </Grid>
                <Grid xs={12} sm={6} md={3}>
                    <StatCard
                        title="Resolved"
                        value={kpiData?.resolvedIssues || 0}
                        icon={<CheckCircleIcon fontSize="small" />}
                        color="success"
                    />
                </Grid>
            </Grid>

            {/* Data Table */}
            <Box sx={{ backgroundColor: 'background.surface', borderRadius: 'md', overflow: 'hidden' }}>
                <Toolbar className="mb-4" left={leftToolbarTemplate} right={rightToolbarTemplate} />
                
                <DataTable
                    value={issues}
                    selection={selectedIssues}
                    onSelectionChange={(e) => setSelectedIssues(e.value)}
                    selectionMode="multiple"
                    dataKey="id"
                    paginator
                    rows={rows}
                    first={first}
                    totalRecords={totalRecords}
                    onPage={onPageChange}
                    loading={loading}
                    lazy
                    className="p-datatable-custom"
                    emptyMessage="No non-compliance issues found"
                    paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                    currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
                    rowsPerPageOptions={[10, 25, 50]}
                >
                    <Column selectionMode="multiple" headerStyle={{ width: '3rem' }} />
                    <Column field="templateName" header="Checklist" sortable />
                    <Column field="questionText" header="Issue Description" sortable />
                    <Column field="nonCompliantOptionText" header="Category" sortable />
                    <Column field="priority" header="Priority" body={priorityBodyTemplate} sortable />
                    <Column field="status" header="Status" body={statusBodyTemplate} sortable />
                    <Column field="assignedToName" header="Assigned To" sortable />
                    <Column field="dueDate" header="Due Date" body={dateBodyTemplate} sortable />
                    <Column body={actionBodyTemplate} header="Actions" style={{ width: '150px' }} />
                </DataTable>
            </Box>

            {/* Mark as Resolved Modal */}
            <Dialog 
                header="Mark Issue as Resolved" 
                visible={resolveModalVisible} 
                style={{ width: '50vw' }} 
                onHide={() => setResolveModalVisible(false)}
                footer={
                    <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                        <Button 
                            label="Cancel" 
                            className="p-button-text" 
                            onClick={() => setResolveModalVisible(false)} 
                        />
                        <Button 
                            label="Mark as Resolved" 
                            className="p-button-success" 
                            onClick={handleResolveIssueSubmit} 
                        />
                    </Box>
                }
            >
                {selectedIssueForAction && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                        <Typography level="body-sm">
                            Are you sure you want to mark this issue as resolved?
                        </Typography>
                        
                        <Typography level="body-sm" sx={{ fontWeight: 'bold' }}>
                            Issue: {selectedIssueForAction.questionText}
                        </Typography>

                        <Box>
                            <Typography level="body-sm" sx={{ mb: 1 }}>Resolution details:</Typography>
                            <InputTextarea 
                                value={resolutionDetails}
                                onChange={(e) => setResolutionDetails(e.target.value)}
                                placeholder="Describe how the issue was resolved..."
                                rows={4}
                                style={{ width: '100%' }}
                            />
                        </Box>

                        <Box>
                            <Typography level="body-sm" sx={{ mb: 1 }}>Resolution date:</Typography>
                            <Calendar
                                value={resolutionDate}
                                onChange={(e) => setResolutionDate(e.value as Date)}
                                dateFormat="yy/mm/dd"
                                placeholder="yyyy/mm/dd"
                                style={{ width: '100%' }}
                            />
                        </Box>

                        <Box>
                            <Typography level="body-sm" sx={{ mb: 1 }}>Resolved by:</Typography>
                            <Dropdown
                                value={resolvedBy}
                                onChange={(e) => setResolvedBy(e.value)}
                                options={[
                                    { label: 'Select a team member', value: '' },
                                    ...siteEmployees.map(employee => ({
                                        label: `${employee.full_name}${employee.title ? ` (${employee.title})` : ''}`,
                                        value: employee.id
                                    }))
                                ]}
                                placeholder={loadingEmployees ? "Loading employees..." : "Select a team member"}
                                disabled={loadingEmployees}
                                style={{ width: '100%' }}
                                filter
                                filterPlaceholder="Search employees..."
                            />
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Checkbox
                                checked={notifyInspector}
                                onChange={(e) => setNotifyInspector(e.checked ?? false)}
                            />
                            <Typography level="body-sm">Notify inspector of resolution</Typography>
                        </Box>
                    </Box>
                )}
            </Dialog>

            {/* Issue History Modal */}
            <Dialog 
                header="Issue History" 
                visible={historyModalVisible} 
                style={{ width: '60vw' }} 
                onHide={() => setHistoryModalVisible(false)}
                footer={
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <Button 
                            label="Close" 
                            className="p-button-text" 
                            onClick={() => setHistoryModalVisible(false)} 
                        />
                    </Box>
                }
            >
                {selectedIssueForAction && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                        <Typography level="body-sm" sx={{ fontWeight: 'bold' }}>
                            Issue: {selectedIssueForAction.questionText}
                        </Typography>

                        <Box>
                            <Typography level="title-sm" sx={{ mb: 2 }}>Audit Log</Typography>
                            <Timeline 
                                value={[
                                    {
                                        status: 'Issue Created',
                                        date: selectedIssueForAction.createdAt,
                                        icon: 'pi pi-plus',
                                        color: '#9C27B0',
                                        description: `${selectedIssueForAction.createdByName} created this issue during checklist submission`
                                    },
                                    {
                                        status: 'Status: Open',
                                        date: selectedIssueForAction.createdAt,
                                        icon: 'pi pi-clock',
                                        color: '#FF9800',
                                        description: 'Issue marked as open and awaiting resolution'
                                    }
                                ]}
                                opposite={(item: any) => (
                                    <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                                        {new Date(item.date).toLocaleDateString()} - {new Date(item.date).toLocaleTimeString()}
                                    </Typography>
                                )}
                                content={(item: any) => (
                                    <Box sx={{ p: 2, backgroundColor: 'background.level1', borderRadius: 'sm' }}>
                                        <Typography level="title-sm">{item.status}</Typography>
                                        <Typography level="body-sm" sx={{ mt: 1 }}>
                                            {item.description}
                                        </Typography>
                                    </Box>
                                )}
                            />
                        </Box>
                    </Box>
                )}
            </Dialog>

            {/* Issue Details Modal */}
            <Dialog 
                header="Issue Details" 
                visible={detailsModalVisible} 
                style={{ width: '70vw' }} 
                onHide={() => setDetailsModalVisible(false)}
                footer={
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <Button 
                            label="Close" 
                            className="p-button-text" 
                            onClick={() => setDetailsModalVisible(false)} 
                        />
                    </Box>
                }
            >
                {selectedIssueForAction && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                        {/* Issue Header */}
                        <Box sx={{ borderBottom: '1px solid', borderColor: 'divider', pb: 2 }}>
                            <Typography level="title-lg" sx={{ mb: 1 }}>
                                {selectedIssueForAction.questionText}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                                <Chip 
                                    color={selectedIssueForAction.priority === 'high' || selectedIssueForAction.priority === 'critical' ? 'danger' : 
                                           selectedIssueForAction.priority === 'medium' ? 'warning' : 'success'}
                                    size="sm"
                                >
                                    {selectedIssueForAction.priority.toUpperCase()}
                                </Chip>
                                <Chip 
                                    color={selectedIssueForAction.status === 'open' ? 'danger' : 
                                           selectedIssueForAction.status === 'in_progress' ? 'warning' : 'success'}
                                    size="sm"
                                >
                                    {selectedIssueForAction.status.replace('_', ' ').toUpperCase()}
                                </Chip>
                            </Box>
                        </Box>

                        {/* Details Grid */}
                        <Grid container spacing={3}>
                            {/* Left Column */}
                            <Grid xs={12} md={6}>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    <Box>
                                        <Typography level="title-sm" sx={{ color: 'text.secondary', mb: 0.5 }}>
                                            Checklist Template
                                        </Typography>
                                        <Typography level="body-md">
                                            {selectedIssueForAction.templateName}
                                        </Typography>
                                    </Box>

                                    <Box>
                                        <Typography level="title-sm" sx={{ color: 'text.secondary', mb: 0.5 }}>
                                            Non-Compliant Option
                                        </Typography>
                                        <Typography level="body-md">
                                            {selectedIssueForAction.nonCompliantOptionText}
                                        </Typography>
                                    </Box>

                                    <Box>
                                        <Typography level="title-sm" sx={{ color: 'text.secondary', mb: 0.5 }}>
                                            Site Information
                                        </Typography>
                                        <Typography level="body-md">
                                            {selectedIssueForAction.siteName}
                                        </Typography>
                                        <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                                            Client: {selectedIssueForAction.clientName}
                                        </Typography>
                                    </Box>

                                    <Box>
                                        <Typography level="title-sm" sx={{ color: 'text.secondary', mb: 0.5 }}>
                                            Assignment
                                        </Typography>
                                        <Typography level="body-md">
                                            {selectedIssueForAction.assignedToName || 'Unassigned'}
                                        </Typography>
                                        {selectedIssueForAction.assignedToEmail && (
                                            <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                                                {selectedIssueForAction.assignedToEmail}
                                            </Typography>
                                        )}
                                    </Box>
                                </Box>
                            </Grid>

                            {/* Right Column */}
                            <Grid xs={12} md={6}>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    <Box>
                                        <Typography level="title-sm" sx={{ color: 'text.secondary', mb: 0.5 }}>
                                            Created
                                        </Typography>
                                        <Typography level="body-md">
                                            {new Date(selectedIssueForAction.createdAt).toLocaleDateString()} at{' '}
                                            {new Date(selectedIssueForAction.createdAt).toLocaleTimeString()}
                                        </Typography>
                                        <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                                            by {selectedIssueForAction.createdByName}
                                        </Typography>
                                    </Box>

                                    <Box>
                                        <Typography level="title-sm" sx={{ color: 'text.secondary', mb: 0.5 }}>
                                            Last Updated
                                        </Typography>
                                        <Typography level="body-md">
                                            {new Date(selectedIssueForAction.updatedAt).toLocaleDateString()} at{' '}
                                            {new Date(selectedIssueForAction.updatedAt).toLocaleTimeString()}
                                        </Typography>
                                    </Box>

                                    <Box>
                                        <Typography level="title-sm" sx={{ color: 'text.secondary', mb: 0.5 }}>
                                            Due Date
                                        </Typography>
                                        <Typography level="body-md">
                                            {selectedIssueForAction.dueDate ? 
                                                new Date(selectedIssueForAction.dueDate).toLocaleDateString() : 
                                                'Not set'
                                            }
                                        </Typography>
                                    </Box>

                                    {selectedIssueForAction.resolvedBy && (
                                        <Box>
                                            <Typography level="title-sm" sx={{ color: 'text.secondary', mb: 0.5 }}>
                                                Resolved By
                                            </Typography>
                                            <Typography level="body-md">
                                                {selectedIssueForAction.resolvedBy}
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>
                            </Grid>
                        </Grid>

                        {/* Notes Section */}
                        {(selectedIssueForAction.notes || selectedIssueForAction.otherCorrectiveAction || selectedIssueForAction.resolutionNotes) && (
                            <Box sx={{ borderTop: '1px solid', borderColor: 'divider', pt: 3 }}>
                                <Typography level="title-sm" sx={{ mb: 2 }}>Additional Information</Typography>
                                
                                {selectedIssueForAction.notes && (
                                    <Box sx={{ mb: 2 }}>
                                        <Typography level="title-sm" sx={{ color: 'text.secondary', mb: 0.5 }}>
                                            Notes
                                        </Typography>
                                        <Typography level="body-md">
                                            {selectedIssueForAction.notes}
                                        </Typography>
                                    </Box>
                                )}

                                {selectedIssueForAction.otherCorrectiveAction && (
                                    <Box sx={{ mb: 2 }}>
                                        <Typography level="title-sm" sx={{ color: 'text.secondary', mb: 0.5 }}>
                                            Other Corrective Action
                                        </Typography>
                                        <Typography level="body-md">
                                            {selectedIssueForAction.otherCorrectiveAction}
                                        </Typography>
                                    </Box>
                                )}

                                {selectedIssueForAction.resolutionNotes && (
                                    <Box sx={{ mb: 2 }}>
                                        <Typography level="title-sm" sx={{ color: 'text.secondary', mb: 0.5 }}>
                                            Resolution Notes
                                        </Typography>
                                        <Typography level="body-md">
                                            {selectedIssueForAction.resolutionNotes}
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        )}

                        {/* IDs Section (for debugging/reference) */}
                        <Box sx={{ borderTop: '1px solid', borderColor: 'divider', pt: 3 }}>
                            <Typography level="title-sm" sx={{ mb: 2 }}>Reference Information</Typography>
                            <Grid container spacing={2}>
                                <Grid xs={12} sm={6}>
                                    <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                                        Issue ID: {selectedIssueForAction.id}
                                    </Typography>
                                </Grid>
                                <Grid xs={12} sm={6}>
                                    <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                                        Submission ID: {selectedIssueForAction.submissionId}
                                    </Typography>
                                </Grid>
                                <Grid xs={12} sm={6}>
                                    <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                                        Template ID: {selectedIssueForAction.templateId}
                                    </Typography>
                                </Grid>
                                <Grid xs={12} sm={6}>
                                    <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                                        Question ID: {selectedIssueForAction.questionId}
                                    </Typography>
                                </Grid>
                            </Grid>
                        </Box>
                    </Box>
                )}
            </Dialog>
        </Fragment>
    );
}

export default NonComplianceTracker;
